// src/pages/admin/SupportTickets.jsx
import React, { useState } from "react";
import PaginationBar from "../ui/PaginationBar";
import { usePagination } from "../../hooks/usePagination";
import SuperLayout from "../layout/SuperAdminLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAllSupportTickets,
  getSupportTicketById,
  replyToSupportTicket,
  updateSupportTicketStatus,
} from "../../services/api"; 

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";

const statusColors = {
  open: "bg-yellow-100 text-yellow-800 hover:bg-yellow-200",
  "in-progress": "bg-blue-100 text-blue-800 hover:bg-blue-200",
  resolved: "bg-green-100 text-green-800 hover:bg-green-200",
  closed: "bg-gray-100 text-gray-800 hover:bg-gray-200",
};

const SupportTickets = () => {
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [loadingReply, setLoadingReply] = useState(false);

  const { data: tickets = [], isLoading, error } = useQuery({
    queryKey: ["support-tickets"],
    queryFn: getAllSupportTickets,
  });

  const { data: selectedTicket } = useQuery({
    queryKey: ["support-ticket", selectedTicketId],
    queryFn: () => getSupportTicketById(selectedTicketId),
    enabled: !!selectedTicketId,
  });

  const replyMutation = useMutation({
    mutationFn: ({ ticketId, message }) => replyToSupportTicket(ticketId, { message }),
    onSuccess: () => {
      queryClient.invalidateQueries(["support-ticket", selectedTicketId]);
      queryClient.invalidateQueries(["support-tickets"]);
      setReplyText("");
      setLoadingReply(false);
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ ticketId, status }) => updateSupportTicketStatus(ticketId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries(["support-tickets"]);
      queryClient.invalidateQueries(["support-ticket", selectedTicketId]);
    },
  });

  const filteredTickets = tickets.filter((ticket) => {
    const matchesSearch =
      searchTerm === "" ||
      ticket.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.user?.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === "all" || ticket.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const { page, pageSize, total, pageItems, setPage, setPageSize } = usePagination(filteredTickets);

  const handleSendReply = () => {
    if (!replyText.trim()) return;
    setLoadingReply(true);
    replyMutation.mutate({ ticketId: selectedTicketId, message: replyText });
  };

  const handleChangeStatus = (newStatus) => {
    if (!selectedTicketId) return;
    statusMutation.mutate({ ticketId: selectedTicketId, status: newStatus });
  };

  if (isLoading) {
    return (
      <SuperLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-lg text-gray-600">Loading support tickets...</p>
        </div>
      </SuperLayout>
    );
  }

  if (error) {
    return (
      <SuperLayout>
        <div className="p-8 text-center text-red-600">
          Failed to load tickets: {error.message || "Unknown error"}
        </div>
      </SuperLayout>
    );
  }

  return (
    <SuperLayout>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Support Tickets</h1>
            <p className="text-gray-600 mt-1">Manage and respond to user support requests</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <Input
            placeholder="Search by ID, subject, name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in-progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tickets Table */}
        <div className="bg-white rounded-xl shadow overflow-hidden border">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">ID</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Subject</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">User</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Created</th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredTickets.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      No tickets found matching your filters.
                    </td>
                  </tr>
                ) : (
                  pageItems.map((ticket) => (
                    <tr key={ticket.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">{ticket.id}</td>
                      <td className="px-6 py-4">{ticket.subject}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={ticket.user?.avatar} />
                            <AvatarFallback>
                              {ticket.user?.name?.charAt(0) || "?"}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{ticket.user?.name}</p>
                            <p className="text-xs text-gray-500">{ticket.user?.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge
                          variant="outline"
                          className={statusColors[ticket.status] || "bg-gray-100 text-gray-800"}
                        >
                          {ticket.status?.replace("-", " ").toUpperCase()}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedTicketId(ticket.id)}
                        >
                          View & Reply
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {filteredTickets.length > 0 && (
            <PaginationBar
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              noun="tickets"
            />
          )}
        </div>
      </div>

      {/* Ticket Detail & Reply Modal */}
      <Dialog open={!!selectedTicketId} onOpenChange={() => setSelectedTicketId(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ticket #{selectedTicket?.id}</DialogTitle>
            <DialogDescription>{selectedTicket?.subject}</DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-6">
            {/* Ticket Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <Badge
                  className={`mt-1 ${statusColors[selectedTicket?.status] || "bg-gray-200"}`}
                >
                  {selectedTicket?.status?.replace("-", " ").toUpperCase()}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-500">User</p>
                <p className="font-medium">{selectedTicket?.user?.name}</p>
                <p className="text-sm text-gray-500">{selectedTicket?.user?.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Created</p>
                <p className="font-medium">
                  {new Date(selectedTicket?.created_at).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Messages Thread */}
            <div className="space-y-6">
              {selectedTicket?.messages?.map((msg, index) => (
                <div
                  key={msg.id || index}
                  className={`flex gap-4 ${
                    msg.sender_type === "admin" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[70%] rounded-2xl p-4 ${
                      msg.sender_type === "admin"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-900"
                    }`}
                  >
                    <p className="font-medium mb-1">
                      {msg.sender_type === "admin" ? "You" : selectedTicket?.user?.name}
                    </p>
                    <p>{msg.content}</p>
                    <p className="text-xs opacity-70 mt-2">
                      {new Date(msg.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Reply Box */}
            <div className="border-t pt-6">
              <Label htmlFor="reply">Your Reply</Label>
              <Textarea
                id="reply"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your response here..."
                className="min-h-[120px] mt-2"
              />

              <div className="flex justify-between items-center mt-4">
                <div className="flex gap-3">
                  <Select
                    value={selectedTicket?.status || ""}
                    onValueChange={(val) => handleChangeStatus(val)}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Change status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in-progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={handleSendReply}
                  disabled={loadingReply || !replyText.trim()}
                >
                  {loadingReply ? "Sending..." : "Send Reply"}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedTicketId(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SuperLayout>
  );
};

export default SupportTickets;